import {
  isCmoeAllocationLedgerEnabled,
  isCmoeActivityModuleEnabled,
  isCmoeProjectionsEnabled,
} from "../../../config/cmoeFeatureFlags";
import { resolveCompetitionIdForRoom } from "../competitionEnsureService";
import { firestoreCompetitionRepository } from "../../../repositories/cmoe/firestoreCompetitionRepository";
import { firestoreMatchRepository } from "../../../repositories/cmoe/firestoreMatchRepository";
import { firestoreAllocationRepository } from "../../../repositories/cmoe/firestoreAllocationRepository";
import { firestoreProjectionRepository } from "../../../repositories/cmoe/firestoreProjectionRepository";
import { listCompetitionGrants } from "../../../repositories/cmoe/firestorePermissionGrantRepository";
import { fetchActivitySnapshot } from "../../../repositories/cmoe/firestoreActivityRepository";
import {
  compareRoomMatchInventory,
  compareMatchStatusAndManager,
  compareParticipantBindings,
  compareMatchCompletion,
  compareAllocationState,
  compareActivityAttachment,
  comparePermissionGrants,
} from "../../../domain/cmoe/shadow/roomSessionParityLogic";
import { PARITY_DIMENSIONS } from "../../../domain/cmoe/shadow/parityTypes";
import { recordCmoeIo } from "./cmoePerformanceBaseline";

/**
 * @typedef {import('../../../domain/cmoe/shadow/parityTypes').ShadowOperation} ShadowOperation
 * @typedef {import('../../../domain/cmoe/shadow/parityTypes').RoomSessionParityReport} RoomSessionParityReport
 */

/**
 * @param {Object} params
 * @param {string} params.roomCode
 * @param {{ createdBy?: string, deputyRefereeUid?: string | null, cmoeCompetitionId?: string } | null} [params.roomDoc]
 * @param {import('../../../domain/roomGovernance/roomGovernanceTypes').RoomSessionMatch[]} [params.roomMatches]
 * @param {ShadowOperation} [params.operation]
 * @param {string | null} [params.focusMatchId]
 * @returns {Promise<RoomSessionParityReport>}
 */
export async function validateRoomSessionParity({
  roomCode,
  roomDoc = null,
  roomMatches = [],
  operation = "Manual",
  focusMatchId = null,
}) {
  const checkedAtMs = Date.now();
  const competitionId = resolveCompetitionIdForRoom(roomCode, roomDoc || {});
  let reads = 0;

  const meta = await firestoreCompetitionRepository.getMeta(competitionId);
  reads += 1;
  const summaries = await firestoreMatchRepository.listSummaries(competitionId);
  reads += summaries.length + 1;

  const activeHolds = isCmoeAllocationLedgerEnabled()
    ? await firestoreAllocationRepository.listActiveHolds(competitionId)
    : [];
  if (isCmoeAllocationLedgerEnabled()) reads += 1;

  const grants = await listCompetitionGrants(competitionId);
  reads += 1;

  recordCmoeIo({ reads });

  /** @type {RoomSessionParityReport['dimensions']} */
  const dimensions = {};

  dimensions.roomMatches = compareRoomMatchInventory(roomMatches, summaries);

  const focusV1 =
    (focusMatchId && roomMatches.find((m) => m.id === focusMatchId)) ||
    (roomMatches.length === 1 ? roomMatches[0] : null);
  const focusSummary = focusMatchId
    ? summaries.find((s) => s.matchId === focusMatchId) || null
    : null;

  if (focusV1) {
    const { status, manager } = compareMatchStatusAndManager(focusV1, focusSummary);
    dimensions.matchStatus = status;
    dimensions.manager = manager;
    dimensions.participantBindings = compareParticipantBindings(focusV1, focusSummary);
    dimensions.matchCompletion = compareMatchCompletion(focusV1, focusSummary);

    let activityDoc = null;
    if (focusV1.status === "live" && isCmoeActivityModuleEnabled()) {
      activityDoc = await fetchActivitySnapshot(focusV1.id);
      reads += 1;
      recordCmoeIo({ reads: 1 });
    }
    dimensions.activityAttachment = compareActivityAttachment(
      focusV1,
      activityDoc,
      isCmoeActivityModuleEnabled()
    );
  } else if (roomMatches.length > 1) {
    const statusMismatches = [];
    for (const v1 of roomMatches) {
      const sum = summaries.find((s) => s.matchId === v1.id);
      const { status, manager } = compareMatchStatusAndManager(v1, sum);
      if (!status.ok || !manager.ok) {
        statusMismatches.push({ matchId: v1.id, status, manager });
      }
    }
    dimensions.matchStatus = {
      ok: statusMismatches.length === 0,
      details: { statusMismatches },
    };
    dimensions.manager = {
      ok: statusMismatches.every((m) => m.manager.ok),
      details: { statusMismatches },
    };
  }

  dimensions.allocation = compareAllocationState(
    roomMatches,
    activeHolds,
    isCmoeAllocationLedgerEnabled()
  );
  dimensions.permissionGrants = comparePermissionGrants(roomDoc || {}, grants);

  if (isCmoeProjectionsEnabled()) {
    const matchList = await firestoreProjectionRepository.getMatchList(competitionId);
    reads += 1;
    recordCmoeIo({ reads: 1 });
    const listOk =
      !matchList ||
      summaries.every((s) => matchList.matches?.some((e) => e.matchId === s.matchId));
    dimensions.projectionMatchList = {
      ok: listOk,
      expected: summaries.map((s) => s.matchId).sort(),
      actual: (matchList?.matches || []).map((e) => e.matchId).sort(),
    };
  }

  let mismatchCount = 0;
  for (const key of PARITY_DIMENSIONS) {
    const row = dimensions[key];
    if (row && !row.skipped && row.ok === false) mismatchCount += 1;
  }
  if (dimensions.projectionMatchList && dimensions.projectionMatchList.ok === false) {
    mismatchCount += 1;
  }

  const metaOk = Boolean(meta && meta.externalRefs?.roomCode === roomCode);

  return {
    ok: mismatchCount === 0 && metaOk,
    roomCode,
    competitionId,
    operation,
    focusMatchId,
    checkedAtMs,
    mismatchCount: metaOk ? mismatchCount : mismatchCount + 1,
    dimensions: {
      ...dimensions,
      competitionMeta: {
        ok: metaOk,
        expected: { roomCode, competitionKind: "room_session" },
        actual: meta,
      },
    },
  };
}
